import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { finalize } from 'rxjs';

import { SenderIdentityApiService } from '../../services/sender-identity-api.service';
import { normalizeApiError } from '../../utils/error-normalizer';

@Component({
  selector: 'app-sender-identity-approve',
  imports: [CommonModule],
  templateUrl: './sender-identity-approve.component.html',
  styleUrl: './sender-identity-approve.component.scss',
})
export class SenderIdentityApproveComponent implements OnInit {
  loading = true;
  actionLoading = false;
  pendingAction: 'approve' | 'decline' | null = null;
  ready = false;
  retryableErrorMessage = '';
  status: 'success' | 'error' | 'info' = 'info';
  message = '';
  private token = '';

  private readonly route = inject(ActivatedRoute);
  private readonly api = inject(SenderIdentityApiService);
  private readonly cdr = inject(ChangeDetectorRef);

  ngOnInit(): void {
    const token = this.route.snapshot.paramMap.get('token')?.trim();
    if (!token) {
      this.loading = false;
      this.ready = false;
      this.status = 'error';
      this.message = 'Approval link is invalid.';
      return;
    }

    this.token = token;
    this.loading = false;
    this.ready = true;
    this.status = 'info';
    this.message = 'Review this sender identity request and choose whether to approve or decline it.';
  }

  approve(): void {
    this.submitDecision('approve');
  }

  decline(): void {
    this.submitDecision('decline');
  }

  private submitDecision(action: 'approve' | 'decline'): void {
    if (!this.token || this.actionLoading) {
      return;
    }

    this.actionLoading = true;
    this.pendingAction = action;
    this.retryableErrorMessage = '';
    this.cdr.markForCheck();

    if (action === 'approve') {
      this.api
        .approveSenderIdentity(this.token)
        .pipe(
          finalize(() => {
            this.actionLoading = false;
            this.pendingAction = null;
            this.cdr.markForCheck();
          }),
        )
        .subscribe({
        next: (response) => {
          this.finishWithTerminalState(
            'success',
            response.status === 'already_verified'
              ? 'Sender identity already verified. You can close this page now.'
              : 'Sender identity verified successfully. You can close this page now.',
          );
        },
        error: (error: unknown) => {
          this.handleDecisionError(error, 'Unable to verify sender identity. The approval link may be invalid or expired.');
        },
        });
      return;
    }

    this.api
      .denySenderIdentity(this.token)
      .pipe(
        finalize(() => {
          this.actionLoading = false;
          this.pendingAction = null;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (response) => {
          this.finishWithTerminalState(
            'success',
            response.status === 'already_denied'
              ? 'Sender identity request was already declined. You can close this page now.'
              : 'Sender identity request declined. You can close this page now.',
          );
        },
        error: (error: unknown) => {
          this.handleDecisionError(error, 'Unable to decline sender identity request. The approval link may be invalid or expired.');
        },
      });
  }

  private handleDecisionError(error: unknown, fallbackMessage: string): void {
    const normalized = normalizeApiError(error, { fallbackMessage });

    if (normalized.kind === 'network' || normalized.kind === 'server') {
      this.retryableErrorMessage = normalized.message;
      this.cdr.markForCheck();
      return;
    }

    this.finishWithTerminalState('error', normalized.message);
  }

  private finishWithTerminalState(status: 'success' | 'error' | 'info', message: string): void {
    this.ready = false;
    this.retryableErrorMessage = '';
    this.status = status;
    this.message = message;
    this.cdr.markForCheck();
  }
}
